import { useEffect, useRef } from 'react';
import { IVehiclePageProps } from '../interfaces';
import { isSkipCrystallize, IVehicleConfiguration } from 'next-common';
import { useClientLazyQuery } from './useClientLazyQuery';
import { NEXT_ENTITY } from '@common/interfaces';
import { useAppDispatch } from '@store/hooks';
import { setPimVehicleList } from '@store/slices/pimVehicleList';
import { setIsVehicleLoading } from '@store/slices/salesWizardSlice';
import { setVehicleConfiguration } from '@store/slices/vehicleConfigurationSlice';

export interface IUseVehicleResponse {
  vehicle: IVehicleConfiguration;
  useVehicleLoading: boolean;
}
export const useVehicle = (props: IVehiclePageProps): IUseVehicleResponse => {
  const dispatch = useAppDispatch();
  const { data, loading } = useVehicleQuery(props);
  let vehicle = null;
  if (data?.length) {
    vehicle = data?.[0];
    dispatch(setVehicleConfiguration(vehicle));
  }
  return { vehicle, useVehicleLoading: loading };
};

const fetchFlagForAPICall = (props: IVehiclePageProps): boolean => {
  let allowAPIcall = null;
  const isSkipPimCall = isSkipCrystallize(props.configuration, props.order);
  if (props.orderId && props.selectedVariantPath) {
    if (props.source) {
      if (!isSkipPimCall) allowAPIcall = true;
      else {
        allowAPIcall = false;
      }
    }
  }
  return allowAPIcall;
};

const useVehicleQuery = (props: IVehiclePageProps) => {
  const dispatch = useAppDispatch();
  const lastFetchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (fetchFlagForAPICall(props)) {
      const fetchKey = [
        props.orderId,
        props.selectedVariantPath,
        props.pimCode,
        props.vehicleId,
        props.serialNo,
        props.source,
        isSkipCrystallize(props.configuration, props.order) ? 'skip' : 'fetch',
      ]
        .map((value) => value ?? '')
        .join('|');
      if (lastFetchKeyRef.current === fetchKey) {
        return;
      }
      lastFetchKeyRef.current = fetchKey;
      vehicleData();
    }
    // vehicleId field is updated when serial no is changed,
    // this condition is required in order for rules to re-run after order mutation
  }, [
    props.orderId,
    props.pimCode,
    props.vehicleId,
    props.currentStep,
    props.source,
    props.configuration,
    props.serialNo,
    props.selectedVariantPath,
  ]);
  const [vehicleData, { loading, data }] = useClientLazyQuery(NEXT_ENTITY.PIM_VEHICLE_LIST, {
    variables: {
      input: {
        path: props.selectedVariantPath,
      },
    },
    // TODO : Originally always network call
    nextOptions: { revalidate: 60 },
    onCompleted: (data) => {
      dispatch(setPimVehicleList(data));
    },
  });
  useEffect(() => {
    dispatch(setIsVehicleLoading(loading));
  }, [loading]);

  return { data: data?.results, loading };
};
